// Adaptive, WCAG-aware color utilities for theme/brand-driven UI.
// Given any background or brand color, derive readable foreground, hover,
// active and border colors automatically — no hardcoded per-component colors.

function clamp(n, lo, hi) { return Math.min(hi, Math.max(lo, n)); }

export function hexToRgb(hex) {
  let h = String(hex || '').trim().replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return { r: 15, g: 98, b: 254 }; // fallback Nova blue
  return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
}

export function rgbToHex({ r, g, b }) {
  const to = (n) => clamp(Math.round(n), 0, 255).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

// Relative luminance per WCAG 2.1
export function luminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  const ch = [r, g, b].map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}

// Contrast ratio between two colors (1..21)
export function contrastRatio(a, b) {
  const la = luminance(a);
  const lb = luminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

export function isLight(hex) { return luminance(hex) > 0.5; }

// Best readable text (near-black or near-white) to place ON a given background.
export function readableTextOn(bg) {
  const black = '#0A0A0A';
  const white = '#FFFFFF';
  return contrastRatio(bg, black) >= contrastRatio(bg, white) ? black : white;
}

// Shade a color toward black (percent<0) or white (percent>0). percent in [-100,100].
export function shade(hex, percent) {
  const { r, g, b } = hexToRgb(hex);
  const p = clamp(percent, -100, 100) / 100;
  const mix = (c) => (p < 0 ? c * (1 + p) : c + (255 - c) * p);
  return rgbToHex({ r: mix(r), g: mix(g), b: mix(b) });
}

// Adjust a brand color so it meets a target contrast ratio against `bg`
// (used when the brand color itself is the foreground, e.g. text/icons on white).
export function adjustForContrast(color, bg = '#FFFFFF', ratio = 4.5) {
  if (contrastRatio(color, bg) >= ratio) return color;
  const bgLight = isLight(bg);
  let out = color;
  // Darken on light backgrounds, lighten on dark backgrounds, step until AA.
  for (let i = 0; i < 20; i += 1) {
    out = shade(out, bgLight ? -8 : 8);
    if (contrastRatio(out, bg) >= ratio) break;
  }
  return out;
}

// One call → all adaptive tokens for a brand/accent color.
// `surface` is the background the accent text/icons render on (default white).
export function accentTokens(accent, surface = '#FFFFFF') {
  const safe = accent || '#0F62FE';
  return {
    accent: safe,                                   // raw brand color (for solid fills/bars/tints)
    onAccent: readableTextOn(safe),                 // text/icons placed ON the accent fill
    onAccentHover: shade(safe, isLight(safe) ? -12 : 14), // accent fill hover bg
    text: adjustForContrast(safe, surface, 4.5),    // accent used AS text on `surface` (AA)
    textOnTint: adjustForContrast(safe, shade(safe, 92), 4.5), // accent icon on its own light tint
    border: adjustForContrast(safe, surface, 3.0),  // accent border (3:1 for non-text/UI)
    soft: `${safe}14`,                              // ~8% tint background
    softHover: `${safe}22`,
  };
}
